import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./types";
import { csrf } from "./csrf";

export const API_URL = (import.meta.env.VITE_API_URL as string) || "";

function genRequestId(): string {
  return crypto.randomUUID();
}

// Tag every request with X-Request-ID and the in-memory CSRF token.
const baseMw: Middleware = {
  async onRequest({ request }) {
    const headers = new Headers(request.headers);
    if (!headers.has("X-Request-ID")) {
      headers.set("X-Request-ID", genRequestId());
    }
    const token = csrf.get();
    if (token && !headers.has("X-CSRF")) {
      headers.set("X-CSRF", token);
    }
    return new Request(request, { headers });
  },
};

// 401 -> POST /auth/refresh -> retry once. /auth/refresh itself returning
// 401 is terminal — emit `auth.unauthorized` so AuthContext can clear `me`
// and redirect to /auth.
const refreshMw: Middleware = {
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    if (request.url.endsWith("/auth/refresh")) return response;

    const refreshResp = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Request-ID": genRequestId() },
    });

    if (!refreshResp.ok) {
      window.dispatchEvent(new CustomEvent("auth.unauthorized"));
      return response;
    }

    try {
      const body = await refreshResp.clone().json();
      if (body?.csrf_token) csrf.set(body.csrf_token);
    } catch {
      // refresh succeeded but body wasn't JSON — fine, we just don't update CSRF.
    }

    // Retry the original request once.
    return fetch(request);
  },
};

export const apiClient = createClient<paths>({
  baseUrl: API_URL,
  credentials: "include",
});
apiClient.use(baseMw, refreshMw);
